import { useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { businessReportsApi, type PnlExpenseBreakdownResponse } from '@/api/businessReports';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import {
  formatExpenseCategoryLabel,
  isPnlCentralExpenseLineId,
  pnlExpenseCategoryHints,
} from '@/lib/pnlExpenseLines';
import { cn } from '@/lib/utils';
import { CentralExpenseReportInclusionCheckbox } from '@/components/business-reports/CentralExpenseReportInclusionCheckbox';

type PnlExpenseBreakdownHoverProps = {
  lineId: string;
  label: string;
  consolidated: boolean;
  periodMode: 'MTD' | 'YTD';
  periodKey: string;
  fyStartYear?: number;
  hospitalId?: string;
  className?: string;
  /** When set, hover target wraps this node (e.g. MoM month cell). */
  children?: ReactNode;
};

function BreakdownPanel({
  data,
  hints,
  loading,
  hintsOnly,
}: {
  data?: PnlExpenseBreakdownResponse;
  hints: ReturnType<typeof pnlExpenseCategoryHints>;
  loading: boolean;
  hintsOnly: boolean;
}) {
  if (!hintsOnly && loading) {
    return <p className="text-xs text-muted-foreground">Loading expenses…</p>;
  }

  return (
    <div className="space-y-2 max-w-md">
      {hintsOnly ? (
        <p className="text-[11px] text-muted-foreground leading-snug">
          Hover a month value for posted expense line items, or use MTD view for the full list.
        </p>
      ) : null}
      {data?.sourceNote || hints?.note ? (
        <p className="text-[11px] text-muted-foreground leading-snug">
          {data?.sourceNote || hints?.note}
        </p>
      ) : null}
      {hints && hints.categories.length > 0 ? (
        <div>
          <p className="text-[11px] font-medium text-slate-700">Expense categories included</p>
          <ul className="mt-1 text-[11px] text-slate-600 list-disc pl-4 space-y-0.5">
            {hints.categories.map((c) => (
              <li key={c}>{formatExpenseCategoryLabel(c)}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {!hintsOnly && data && data.categorySummary.length > 0 ? (
        <div>
          <p className="text-[11px] font-medium text-slate-700">
            Posted in period ({data.periodLabel})
          </p>
          <ul className="mt-1 text-[11px] space-y-0.5">
            {data.categorySummary.map((c) => (
              <li key={c.category} className="flex justify-between gap-3 tabular-nums">
                <span className="text-slate-600 truncate">{c.label}</span>
                <span className="font-medium shrink-0">
                  ₹{c.amount.toLocaleString('en-IN')}
                  <span className="text-muted-foreground font-normal"> · {c.count}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {!hintsOnly && data && data.items.length > 0 ? (
        <div>
          <p className="text-[11px] font-medium text-slate-700">
            Line items ({data.items.length}
            {data.items.length >= 500 ? '+' : ''})
          </p>
          <ul className="mt-1 max-h-52 overflow-y-auto text-[11px] divide-y divide-slate-100">
            {data.items.map((item) => {
              const included = item.includedInReports !== false;
              return (
                <li
                  key={item.id}
                  className={cn(
                    'py-1.5 flex justify-between gap-2 items-start',
                    !included && 'opacity-60'
                  )}
                >
                  <div className="flex gap-2 min-w-0 flex-1">
                    <CentralExpenseReportInclusionCheckbox
                      expenseId={item.id}
                      includedInReports={included}
                    />
                    <div className="min-w-0">
                      <p
                        className={cn(
                          'font-medium truncate',
                          included ? 'text-slate-800' : 'text-slate-500 line-through'
                        )}
                      >
                        {item.description ||
                          item.vendorName ||
                          item.expenseEmployeeName ||
                          item.categoryLabel}
                      </p>
                      <p className="text-muted-foreground truncate">
                        {item.categoryLabel}
                        {item.vendorName ? ` · ${item.vendorName}` : ''}
                        {item.serviceMonth ? ` · ${item.serviceMonth}` : ''}
                        {!included ? ' · Excluded from reports' : ''}
                      </p>
                    </div>
                  </div>
                  <span className="tabular-nums font-medium shrink-0">
                    ₹{item.amount.toLocaleString('en-IN')}
                  </span>
                </li>
              );
            })}
          </ul>
          {data.total > 0 ? (
            <p className="text-[11px] font-semibold text-right tabular-nums pt-1 border-t mt-1">
              Total ₹{data.total.toLocaleString('en-IN')}
            </p>
          ) : null}
        </div>
      ) : !hintsOnly && data && !loading ? (
        <p className="text-[11px] text-muted-foreground">No central expenses posted for this period.</p>
      ) : null}
    </div>
  );
}

export function PnlExpenseBreakdownHover({
  lineId,
  label,
  consolidated,
  periodMode,
  periodKey,
  fyStartYear,
  hospitalId,
  className,
  children,
}: PnlExpenseBreakdownHoverProps) {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hints = pnlExpenseCategoryHints(lineId, consolidated);
  const hintsOnly = !periodKey;
  const canFetchLineItems =
    !hintsOnly &&
    isPnlCentralExpenseLineId(lineId) &&
    (periodMode === 'MTD' || !!fyStartYear);

  const { data, isLoading } = useQuery({
    queryKey: [
      'pnl-expense-breakdown',
      lineId,
      periodMode,
      periodKey,
      fyStartYear,
      hospitalId,
      consolidated,
    ],
    queryFn: () =>
      businessReportsApi.pnlExpenseBreakdown({
        lineId,
        periodMode,
        month: periodMode === 'MTD' ? periodKey : undefined,
        fy: periodMode === 'YTD' ? fyStartYear : undefined,
        hospitalId,
      }),
    enabled: open && canFetchLineItems,
    staleTime: 60_000,
  });

  if (!hints) {
    return <>{children ?? <span className={className}>{label}</span>}</>;
  }

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  };

  const handleEnter = () => {
    clearCloseTimer();
    setOpen(true);
  };

  return (
    <Popover open={open} modal={false}>
      <PopoverAnchor asChild>
        <span
          className={cn(
            'inline-flex items-center gap-1',
            children ? 'cursor-help w-full justify-end' : 'cursor-help',
            className
          )}
          onMouseEnter={handleEnter}
          onMouseLeave={scheduleClose}
          onFocus={handleEnter}
          onBlur={scheduleClose}
          tabIndex={children ? -1 : 0}
          role={children ? undefined : 'button'}
          aria-label={children ? undefined : `${label} — expense breakdown`}
        >
          {children ?? (
            <>
              <span className="border-b border-dotted border-slate-400">{label}</span>
              <Info className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            </>
          )}
        </span>
      </PopoverAnchor>
      <PopoverContent
        className="w-[min(24rem,calc(100vw-2rem))] p-3"
        align={children ? 'end' : 'start'}
        side="bottom"
        sideOffset={6}
        collisionPadding={12}
        onMouseEnter={handleEnter}
        onMouseLeave={scheduleClose}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <p className="text-xs font-semibold text-slate-900 mb-2">{label}</p>
        <BreakdownPanel
          data={data}
          hints={hints}
          loading={isLoading}
          hintsOnly={hintsOnly}
        />
      </PopoverContent>
    </Popover>
  );
}
